import { useCallback, useEffect, useState } from 'react';
import diff from 'html-diff-ts';
import ContentManagementInterface from '../../models/ContentManagementInterface';
import { waitForImages } from './contentDiffComparisonUtils';

const RENDER_SETTLE_MS = 2000;
const DIFF_APPLY_DELAY_MS = 100;

interface UseContentDiffCalculationOptions {
  originalContent: ContentManagementInterface;
  newContent: ContentManagementInterface;
  originalRef: HTMLDivElement | null;
  newRef: HTMLDivElement | null;
  isUserReady: boolean;
}

export function useContentDiffCalculation({
  originalContent,
  newContent,
  originalRef,
  newRef,
  isUserReady,
}: UseContentDiffCalculationOptions) {
  const [isCalculating, setIsCalculating] = useState(true);
  const [diffHtml, setDiffHtml] = useState('');
  const [error, setError] = useState<string | null>(null);

  const calculateDiff = useCallback(async () => {
    if (!isUserReady || !originalRef || !newRef) {
      return;
    }

    try {
      setIsCalculating(true);
      setError(null);

      await new Promise((resolve) => setTimeout(resolve, RENDER_SETTLE_MS));
      await Promise.all([waitForImages(originalRef), waitForImages(newRef)]);

      const originalHtml = originalRef.innerHTML;
      const newHtml = newRef.innerHTML;
      const diffResult = diff(originalHtml, newHtml);

      setTimeout(() => {
        setDiffHtml(diffResult);
      }, DIFF_APPLY_DELAY_MS);
    } catch (err) {
      console.error('Error calculating diff:', err);
      setError(err instanceof Error ? err.message : 'Failed to calculate diff');
    } finally {
      setIsCalculating(false);
    }
  }, [originalContent, newContent, originalRef, newRef, isUserReady]);

  useEffect(() => {
    calculateDiff();
  }, [calculateDiff]);

  return { isCalculating, diffHtml, error };
}
